import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate as useNav } from 'react-router-dom';
import { adminApi, companyApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
  Legend, AreaChart, Area, LabelList,
} from 'recharts';

/* ── helpers ─────────────────────────────────────────────────────────────── */
const v = (n) => Number(n ?? 0);
const formatCount = (n) => v(n).toLocaleString('en-US');

const ACTIVITY_SERIES = [
  { key: 'In Process',   color: '#3b82f6', gradientId: 'gInProcess' },
  { key: 'Awaiting CVA', color: '#d97706', gradientId: 'gAwaitingCVA' },
  { key: 'Approved',     color: '#10b981', gradientId: 'gApproved' },
  { key: 'Rejected',     color: '#ef4444', gradientId: 'gRejected' },
  { key: 'Cancelled',    color: '#9ca3af', gradientId: 'gCancelled' },
];

/* ── Custom tooltip ──────────────────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="flex items-center gap-1.5" style={{ color: p.color }}>
          <span className="h-2 w-2 rounded-full inline-block" style={{ background: p.color }} />
          {p.name}: <span className="font-bold ml-0.5">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

/* ── Status cards config ─────────────────────────────────────────────────── */
const STATUS_CARDS = (s) => [
  {
    label: 'New',
    sublabel: 'Awaiting assignment',
    value: v(s?.newRequests),
    from: '#f59e0b', to: '#fbbf24',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" strokeLinecap="round" />
      </svg>
    ),
    filter: 'NEW',
  },
  {
    label: 'Assigned',
    sublabel: 'Sent to vendor',
    value: v(s?.assignedRequests),
    from: '#3b82f6', to: '#6366f1',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M4 4h16v4H4zM4 10h10v4H4zM4 16h6v4H4z" strokeLinejoin="round" />
      </svg>
    ),
    filter: 'ASSIGNED',
  },
  {
    label: 'Awaiting OmniDrive Review',
    sublabel: 'Vendor submitted BOM',
    value: v(s?.vendorSubmitted),
    from: '#7c3aed', to: '#a78bfa',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    filter: 'VENDOR_SUBMITTED',
  },
  {
    label: 'Account Review',
    sublabel: 'Awaiting account review',
    value: v(s?.companyApproved),
    from: '#0891b2', to: '#22d3ee',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    filter: 'COMPANY_APPROVED',
  },
  {
    label: 'Awaiting CVA',
    sublabel: 'Pending final CVA approval',
    value: v(s?.pendingFinalApproval),
    from: '#f59e0b', to: '#d97706',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    filter: 'PENDING_FINAL_APPROVAL',
  },
  {
    label: 'Fully Approved',
    sublabel: 'CVA final approval given',
    value: v(s?.clientApproved),
    from: '#10b981', to: '#34d399',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    filter: 'CLIENT_APPROVED',
  },
  {
    label: 'Rejected',
    sublabel: 'Company or client rejected',
    value: v(s?.rejected),
    from: '#ef4444', to: '#f87171',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
      </svg>
    ),
    filter: 'REJECTED',
  },
  {
    label: 'Cancelled',
    sublabel: 'Process permanently cancelled',
    value: v(s?.cancelled),
    from: '#6b7280', to: '#9ca3af',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-white" stroke="currentColor" strokeWidth={2}>
        <path d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    filter: 'CANCELLED',
  },
];

/* ── Bar chart config ────────────────────────────────────────────────────── */
const BAR_DATA = (s) => [
  { name: 'New',          value: v(s?.newRequests),           fill: '#f59e0b' },
  { name: 'Assigned',     value: v(s?.assignedRequests),      fill: '#3b82f6' },
  { name: 'Responded',    value: v(s?.vendorSubmitted),       fill: '#8b5cf6' },
  { name: 'Account Review', value: v(s?.companyApproved),     fill: '#0891b2' },
  { name: 'Awaiting CVA', value: v(s?.pendingFinalApproval),  fill: '#d97706' },
  { name: 'Approved',     value: v(s?.clientApproved),        fill: '#10b981' },
  { name: 'Rejected',     value: v(s?.rejected),              fill: '#ef4444' },
  { name: 'Cancelled',    value: v(s?.cancelled),             fill: '#9ca3af' },
];

/* ── Filter bar ──────────────────────────────────────────────────────────── */
function FilterBar({ dateFrom, dateTo, clientId, clients, onChange, onReset }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4">
      <div className="flex flex-wrap items-end gap-4">

        {/* Date from */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onChange('dateFrom', e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Date to */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onChange('dateTo', e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* Account selector */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Account</label>
          <select
            value={clientId}
            onChange={(e) => onChange('clientId', e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[160px]"
          >
            <option value="">All accounts</option>
            {(clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>{c.companyName}</option>
            ))}
          </select>
        </div>

        {/* Reset */}
        <button
          onClick={onReset}
          className="text-xs font-medium text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-2 transition-colors"
        >
          Reset
        </button>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function CompanyDashboard() {
  const navigate = useNav();

  /* ── filter state ──────────────────────────────────────────────────────── */
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [clientId, setClientId] = useState('');

  const handleFilterChange = (key, val) => {
    if (key === 'dateFrom') setDateFrom(val);
    if (key === 'dateTo')   setDateTo(val);
    if (key === 'clientId') setClientId(val);
  };

  const handleReset = () => { setDateFrom(''); setDateTo(''); setClientId(''); };

  /* ── build query params ────────────────────────────────────────────────── */
  const statsParams = useMemo(() => {
    const p = {};
    if (dateFrom)  p.dateFrom  = dateFrom;
    if (dateTo)    p.dateTo    = dateTo;
    if (clientId)  p.clientId  = clientId;
    return p;
  }, [dateFrom, dateTo, clientId]);

  const trendParams = useMemo(() => {
    const p = { months: 6 };
    if (clientId) p.clientId = clientId;
    return p;
  }, [clientId]);

  /* ── data fetching ─────────────────────────────────────────────────────── */
  const { data: statsData, isLoading: statsLoading } = useQuery(
    ['admin-stats', statsParams],
    () => adminApi.getDashboardStats(statsParams),
    { refetchInterval: 60000 }
  );

  const { data: trendData, isLoading: trendLoading } = useQuery(
    ['admin-trend', trendParams],
    () => adminApi.getActivityTrend(trendParams),
    { refetchInterval: 60000 }
  );

  // Fetch client companies for the filter dropdown
  const { data: clientsData } = useQuery(
    'client-companies-list',
    () => companyApi.getAllCompanies({ type: 'CLIENT', active: true, size: 200 }),
    { staleTime: 5 * 60 * 1000 }
  );

  const stats   = statsData?.data ?? statsData;
  const rawTrend = trendData?.data ?? trendData;
  const trendPoints = Array.isArray(rawTrend) ? rawTrend : [];
  const clients  = clientsData?.content ?? clientsData?.data?.content ?? [];

  const barData = useMemo(() => BAR_DATA(stats), [stats]);

  // Map backend keys to chart-friendly names (New excluded — zero workflow activity)
  const lineData = useMemo(() => trendPoints.map((pt) => ({
    month:           pt.month,
    'In Process':    Number(pt.inProcess           ?? 0),
    'Awaiting CVA':  Number(pt.pendingFinalApproval ?? 0),
    'Approved':      Number(pt.approved             ?? 0),
    'Rejected':      Number(pt.rejected             ?? 0),
    'Cancelled':     Number(pt.cancelled            ?? 0),
  })), [trendPoints]);

  // Hide inactive zero-value series so the legend and chart stay readable.
  const activeActivitySeries = useMemo(
    () => ACTIVITY_SERIES.filter(({ key }) => lineData.some((point) => point[key] > 0)),
    [lineData]
  );

  const isFiltered = !!(dateFrom || dateTo || clientId);

  if (statsLoading) return <LoadingSpinner message="Loading dashboard…" />;

  return (
    <div className="space-y-5 pb-8">

      {/* ── Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">OMNiDRIVE Dashboard</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            {isFiltered ? 'Filtered view' : 'Full workflow overview'} · auto-refreshes every 60 s
          </p>
        </div>
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-100">
          {v(stats?.totalRequests)} total requests
        </span>
      </div>

      {/* ── Filters ── */}
      <FilterBar
        dateFrom={dateFrom}
        dateTo={dateTo}
        clientId={clientId}
        clients={clients}
        onChange={handleFilterChange}
        onReset={handleReset}
      />

      {/* ── Status Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-8 gap-4">
        {STATUS_CARDS(stats).map((card) => (
          <button
            key={card.label}
            onClick={() => card.filter && navigate(`/platform/finished-goods?status=${card.filter}${clientId ? '&clientId=' + clientId : ''}`)}
            className="relative overflow-hidden rounded-2xl p-5 text-left group transition-transform hover:-translate-y-0.5 hover:shadow-lg"
            style={{ background: `linear-gradient(135deg, ${card.from}, ${card.to})` }}
          >
            <div className="absolute -right-4 -top-4 h-20 w-20 rounded-full bg-white opacity-10" />
            <div className="absolute -right-1 -bottom-6 h-24 w-24 rounded-full bg-white opacity-10" />
            <div className="absolute top-3 right-3 bg-white bg-opacity-20 rounded-xl p-2 group-hover:bg-opacity-30 transition-colors">
              {card.icon}
            </div>
            <div className="relative pr-9">
              <p className="text-white text-opacity-80 text-xs font-medium uppercase tracking-wide leading-tight">{card.label}</p>
              <p className="text-white text-4xl font-black mt-1 leading-none">{card.value}</p>
              <p className="text-white text-opacity-70 text-xs mt-1.5">{card.sublabel}</p>
            </div>
            {card.filter && (
              <p className="relative text-white text-opacity-70 text-xs mt-3 font-medium">View details →</p>
            )}
          </button>
        ))}
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        {/* Status Distribution bar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Status Distribution</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {isFiltered ? 'Filtered counts' : 'Current counts across all states'}
              </p>
            </div>
            <span className="text-xs bg-gray-50 text-gray-500 border border-gray-100 rounded-lg px-2 py-1">Bar</span>
          </div>
          <ResponsiveContainer width="100%" height={304}>
            <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 42, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis type="number" allowDecimals={false} tickFormatter={formatCount}
                tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={108}
                tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f9fafb' }} />
              <Bar dataKey="value" minPointSize={3} maxBarSize={18} radius={[0, 6, 6, 0]}>
                {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                <LabelList dataKey="value" position="right" formatter={formatCount}
                  fill="#64748b" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Activity Trend area */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Activity Trend</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {isFiltered && clientId ? 'Filtered by client · ' : ''}6-month request activity
              </p>
            </div>
            {trendLoading && (
              <span className="text-xs text-gray-400 animate-pulse">Loading…</span>
            )}
            {!trendLoading && (
              <span className="text-xs bg-gray-50 text-gray-500 border border-gray-100 rounded-lg px-2 py-1">Area</span>
            )}
          </div>

          {activeActivitySeries.length === 0 ? (
            <div className="flex items-center justify-center h-[230px] text-gray-300 text-sm">
              No workflow activity in the last 6 months
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={246}>
              <AreaChart data={lineData} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
                <defs>
                  {activeActivitySeries.map(({ gradientId, color }) => (
                    <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="10%" stopColor={color} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="month" padding={{ left: 8, right: 8 }}
                  tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                {activeActivitySeries.map(({ key, color, gradientId }) => (
                  <Area key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2}
                    fill={`url(#${gradientId})`} dot={{ r: 2.5, strokeWidth: 1 }} activeDot={{ r: 4 }} />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Quick Actions ── */}
      <div>
        <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          {[
            { label: 'Finished Goods',   desc: 'Browse & assign vendors',         path: '/platform/finished-goods', emoji: '📦', accent: 'blue'   },
            { label: 'Review Queue',     desc: 'Approve or reject submissions',    path: '/platform/review',         emoji: '🔍', accent: 'indigo' },
            { label: 'Companies',        desc: 'Manage Accounts & Vendors', path: '/platform/companies',      emoji: '🏢', accent: 'amber'  },
            { label: 'User Management',  desc: 'Platform & company accounts',      path: '/platform/users',          emoji: '👥', accent: 'purple' },
          ].map((a) => (
            <button
              key={a.path}
              onClick={() => navigate(a.path)}
              className={`bg-white rounded-2xl border border-gray-100 p-5 text-left hover:shadow-md transition-all group`}
            >
              <span className="text-2xl mb-3 block">{a.emoji}</span>
              <p className="font-bold text-gray-900 text-sm group-hover:text-blue-600 transition-colors">{a.label}</p>
              <p className="text-xs text-gray-400 mt-1 leading-snug">{a.desc}</p>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}
